import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import TurnstileWrapper, { TurnstileWrapperRef } from '@/components/common/Turnstile';

export interface TurnstilePortalProps {
  /** 是否显示不可见式验证 */
  showInvisible: boolean;

  /** 是否显示托管式验证 */
  showManaged: boolean;

  /** 不可见式验证组件的ref */
  invisibleRef: React.RefObject<TurnstileWrapperRef>;

  /** 不可见式验证成功回调 */
  onInvisibleSuccess: (token: string) => void;

  /** 不可见式验证失败回调 */
  onInvisibleError: () => void;

  /** 托管式验证成功回调 */
  onManagedSuccess: (token: string) => void;

  /** 托管式验证失败回调 */
  onManagedError: () => void;

  /** Portal目标容器选择器,默认为 '[data-turnstile-container]' */
  containerSelector?: string;
  enable?: boolean; // 是否启用验证组件,默认为true
}

/**
 * Turnstile Portal组件
 *
 * 通过createPortal将Turnstile验证组件渲染到统一的容器中
 * 这样可以避免在每个登录组件中重复渲染Turnstile组件
 *
 * @example
 * ```tsx
 * <TurnstilePortal
 *   showInvisible={showInvisibleTurnstile}
 *   showManaged={showManagedTurnstile}
 *   invisibleRef={invisibleTurnstileRef}
 *   onInvisibleSuccess={handleInvisibleSuccess}
 *   onInvisibleError={handleInvisibleError}
 *   onManagedSuccess={handleManagedSuccess}
 *   onManagedError={handleManagedError}
 * />
 * ```
 */
export const TurnstilePortal: React.FC<TurnstilePortalProps> = ({
  enable = false,
  showInvisible,
  showManaged,
  invisibleRef,
  onInvisibleSuccess,
  onInvisibleError,
  onManagedSuccess,
  onManagedError,
  containerSelector = '[data-turnstile-container]',
}) => {
  const [container, setContainer] = useState<Element | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  // 查找Portal容器
  useEffect(() => {
    const element = document.querySelector(containerSelector);
    setContainer(element);
    setIsMounted(true);
  }, [containerSelector]);

  // 未挂载时不渲染
  if (!isMounted || !enable) {
    return null;
  }

  const content = (
    <>
      {/* 不可见式验证 - 隐藏在屏幕外 */}
      {showInvisible && (
        <div style={{ position: 'absolute', left: '-9999px' }}>
          <TurnstileWrapper
            ref={invisibleRef}
            mode="invisible"
            onSuccess={onInvisibleSuccess}
            onError={onInvisibleError}
          />
        </div>
      )}

      {/* 托管式验证 - 显示在容器中 */}
      {showManaged && (
        <div className="mt-4">
          <TurnstileWrapper
            mode="managed"
            onSuccess={onManagedSuccess}
            onError={onManagedError}
          />
        </div>
      )}
    </>
  );

  // 如果找到了容器,使用Portal;否则直接渲染(降级方案)
  if (container) {
    return createPortal(content, container);
  }

  return content;
};

export default TurnstilePortal;
